import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { useReveal } from "../hooks/useReveal";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";

const values = [
  { title: "Integrity above all", text: "We do what we say we will. Honest advice, transparent pricing and no hidden surprises \u2014 integrity underpins every relationship and every project." },
  { title: "A professional approach", text: "Clear estimates, regular updates and a single, professional point of contact throughout your build, delivered by a small team of experienced tradesmen." },
  { title: "Uncompromising quality", text: "We work with authentic materials and time-honoured techniques, taking pride in getting the details right on period, listed and modern homes alike." },
  { title: "Association for Project Management", text: "We are full members of the Association for Project Management, working to recognised professional standards for complete peace of mind.", badge: true },
];

const About = () => {
  const { company, stats, process } = useContent();
  const r1 = useReveal();
  const r2 = useReveal();
  return (
    <main className="pt-[76px]">
      <Seo
        title="About Us"
        description="A family-run building contractor with over 25 years' experience. Meet the team behind Fletcher & Stone and the values of integrity, professionalism and quality that guide every project."
        path="/about"
      />
      <PageHero
        eyebrow="About Us"
        title="Professional builders serving London & the shires"
        image="https://images.unsplash.com/photo-1517581177682-a085bb7ffb15"
      />

      <section className="max-w-[1240px] mx-auto px-5 md:px-8 py-24 md:py-32">
        <div ref={r1.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${r1.className}`}>
          <div className="lg:col-span-6">
            <img src="https://images.unsplash.com/photo-1607900840367-a83dcd63296d" alt="Restored period home" className="w-full h-[520px] object-cover" />
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="eyebrow mb-5">Our story</p>
            <h2 className="section-title text-[32px] md:text-[42px] mb-7">Built on an obsession with quality workmanship</h2>
            <p className="font-body text-[15.5px] leading-[2] text-[#5b564d] mb-5">
              Fletcher &amp; Stone brings over 25 years of industry experience, founded on a simple principle &mdash; that fine homes deserve to be built and cared for by professionals who value integrity and quality above all.
            </p>
            <p className="font-body text-[15.5px] leading-[2] text-[#5b564d] mb-5">
              As a family-run building contractor based in Tring, Hertfordshire, we undertake new builds, heavy structural alterations, refurbishments, extensions &amp; conversions for clients in London, The Home Counties and The Cotswolds. We provide professional in-house project management services to every project, ensuring our clients are fully apprised throughout the project. We are known for our attention to detail, honesty and the enduring quality of our work.
            </p>
            <p className="font-body text-[15.5px] leading-[2] text-[#5b564d]">
              We take pride in getting the details right, communicating clearly and honestly, working cleanly and considerately, and leaving behind homes built to the highest standard for generations to come.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#f6f2ec] py-20">
        <div className="max-w-[1240px] mx-auto px-5 md:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              {s.badge ? (
                <img src={company.apmBadge} alt="Association for Project Management - Full Member (MAPM)" className="h-16 md:h-[74px] w-auto mx-auto" />
              ) : (
                <div className="font-display text-[#94664f] text-[52px] md:text-[64px] leading-none font-light">{s.value}</div>
              )}
              <div className="overline mt-3">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1240px] mx-auto px-5 md:px-8 py-24 md:py-32">
        <div ref={r2.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 ${r2.className}`}>
          <div className="lg:col-span-4">
            <p className="eyebrow mb-5">Why choose us</p>
            <h2 className="section-title text-[32px] md:text-[44px]">The values behind every build</h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
            {values.map((v, i) => (
              <div key={i}>
                <div className="flex items-start gap-3 mb-3">
                  <span className="w-8 h-8 rounded-full bg-[#94664f]/10 flex items-center justify-center shrink-0 mt-0.5"><Check size={15} className="text-[#94664f]" /></span>
                  <h3 className="font-display text-[24px] text-[#23211e]">{v.title}</h3>
                </div>
                <p className="font-body text-[14.5px] leading-[1.95] text-[#6b665d] pl-11">{v.text}</p>
                {v.badge && (
                  <img src={company.apmBadge} alt="Association for Project Management - Full Member (MAPM)" className="h-20 w-auto mt-5 ml-11" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1c1a17] py-24 md:py-32">
        <div className="max-w-[1240px] mx-auto px-5 md:px-8">
          <div className="mb-16">
            <p className="eyebrow mb-5">Our process</p>
            <h2 className="section-title !text-white text-[34px] md:text-[46px] max-w-[520px]">From first meeting to final handover</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#332f2a]">
            {process.map((p) => (
              <div key={p.step} className="bg-[#1c1a17] p-9">
                <div className="font-display text-[#94664f] text-[46px] font-light leading-none">{p.step}</div>
                <h3 className="font-display text-white text-[24px] mt-5 mb-3">{p.title}</h3>
                <p className="font-body text-[14px] leading-[1.9] text-[#a49e94]">{p.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-16">
            <Link to="/contact" className="btn-primary !bg-[#94664f] hover:!bg-white hover:!text-[#23211e]">
              Discuss your project <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
